import {
  UpdateMultipleDirectorInputType,
  updateMultipleDirectors,
  useGetAllDirectorsInCompany,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { PlusIcon, ProceedIcon } from '@commenda/icons';
import {
  Button,
  Loading,
  LoadingIndicator,
  MultiSelect,
  useToggle,
} from '@commenda/kakashi';
import { defaultAddressSchema } from '@commenda/schema/address';
import { TSetNotification } from '@commenda/types/src/random';
import {
  DirectorBase,
  WorkRequestResponseType,
} from '@commenda/types/src/work-request/types';
import { classNames } from '@commenda/utils';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { getDirectorName } from '../Director/common';
import DirectorModal from '../Director/new/DirectorModal';
import { DirectorCardKS, IncorporationFormHeading } from '../common';
import { USResponsiblePartyForm } from './USResponsiblePartyForm';
import { validateResponsiblePartyInfo } from './validators';

const ResponsiblePartyInfo = ({
  next,
  previous,
  data,
  mutate,
  companyId,
  corporationId,
  token,
  setNotification,
}: {
  next: () => void;
  previous: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  companyId: number;
  corporationId: number;
  token: string;
  setNotification: TSetNotification;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const [isSubmitting, setSubmitting] = useState(false);

  const [directors, setDirectors] = useState<DirectorBase[]>([]);

  const {
    data: allCompanyDirectors,
    error,
    mutate: companyMutate,
  } = useGetAllDirectorsInCompany(data?.companyId);

  const directorModalToggle = useToggle(false);
  const [selectedDirectorIds, setSelectedDirectorIds] = useState<number[]>([]);
  const selectedDirectorIdsRef = useRef<number[]>([]);

  const [directorIdsWithError, setDirectorIdsWithError] = useState<Set<number>>(
    new Set()
  );

  const [directorToEdit, setDirectorToEdit] = useState<DirectorBase | null>(
    null
  );

  useEffect(() => {
    setDirectors(
      allCompanyDirectors?.directors?.map((eachDirector) => {
        return {
          id: eachDirector?.id,
          directorFirstName: eachDirector.directorFirstName,
          directorMiddleName: eachDirector.directorMiddleName,
          directorLastName: eachDirector.directorLastName,
          email: eachDirector.email,
          operatingAddress:
            eachDirector.operatingAddress || defaultAddressSchema,
          number: eachDirector.number,
          corporationHasDirector: eachDirector.corporationHasDirector,
          dateOfBirth: eachDirector?.dateOfBirth,
          governmentId: eachDirector?.governmentId,
          directorFiles: eachDirector?.directorFiles,
          ssnMask: eachDirector?.ssnMask,
          hasNoSSN: eachDirector?.hasNoSSN,
        };
      }) || []
    );
  }, [allCompanyDirectors]);

  useEffect(() => {
    let alreadySelectedDirectors = [...selectedDirectorIdsRef.current];

    allCompanyDirectors?.directors
      ?.filter((dir) =>
        dir.corporationHasDirector?.find(
          (chd) =>
            chd.corporationId === corporationId &&
            chd.directorId === dir.id &&
            chd.additionalDirectorInformation?.isResponsibleParty
        )
      )
      .map((director) => {
        if (director?.WorkRequests?.find((wrkReq) => wrkReq.id === id)) {
          if (!alreadySelectedDirectors.includes(director.id))
            alreadySelectedDirectors = [director.id];
        }
      });

    setSelectedDirectorIds(alreadySelectedDirectors);
  }, [allCompanyDirectors]);

  if (!allCompanyDirectors) {
    if (error) {
      console.error(error);
    }
    return <Loading />;
  }
  const isDirector = (directorId: number) => {
    const director = allCompanyDirectors?.directors?.find(
      (dir) => dir?.id === directorId
    );

    const corporationHasDirector = director?.corporationHasDirector?.find(
      (chd) =>
        chd.corporationId === data.corporationId &&
        chd.directorId === directorId
    );

    return corporationHasDirector?.additionalDirectorInformation?.isDirector;
  };

  const onSubmit = async () => {
    setSubmitting(true);

    if (!data?.id) {
      setNotification({
        show: true,
        valid: false,
        message: 'Work request ID is missing. Please refresh the page.',
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    if (selectedDirectorIds.length === 0) {
      setNotification({
        show: true,
        valid: false,
        message: `US Entity requires you to have one assigned responsible party.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    const dirsWithHasNoSSNFieldAsNull = directors.filter(
      (dir) => dir.hasNoSSN === null && selectedDirectorIds.includes(dir.id)
    );

    if (dirsWithHasNoSSNFieldAsNull.length) {
      setDirectorToEdit(dirsWithHasNoSSNFieldAsNull[0]);
      directorModalToggle.on();

      setNotification({
        show: true,
        valid: false,
        message: `Please indicate if the responsible party has a SSN or ITIN.`,
        title: 'Error',
      });

      const dirsWithNullHasNoSSNIds = new Set(
        dirsWithHasNoSSNFieldAsNull.map((d) => d.id)
      );

      setDirectorIdsWithError(dirsWithNullHasNoSSNIds);

      setSubmitting(false);
      return;
    }

    const formError = validateResponsiblePartyInfo(
      directors.map((dir) => ({
        ...dir,
        SSINorITIN: dir.ssnMask || null,
        hasSSINorITIN: !!dir.ssnMask,
        id: dir.id,
      })),
      selectedDirectorIds
    );

    if (formError.length) {
      setDirectorIdsWithError(new Set(formError.map((fe) => fe.id)));

      const firstDirectorWithError = directors.find((dir) =>
        formError.some((fe) => fe.id === dir.id)
      );
      if (firstDirectorWithError) {
        setDirectorToEdit(firstDirectorWithError);
        directorModalToggle.on();
      }

      setNotification({
        show: true,
        valid: false,
        message: `Please fill in the missing information for responsible parties.`,
        title: 'Error',
      });
      setSubmitting(false);
      return;
    }

    // List of directors that are currently assigned as responsible party but are no longer selected
    const directorsToRemoveAsResponsibleParty = allCompanyDirectors?.directors
      ?.filter((eachDirector) =>
        eachDirector?.corporationHasDirector?.find(
          (dirWithCorp) =>
            dirWithCorp.corporationId === data.corporationId &&
            dirWithCorp.directorId === eachDirector.id &&
            dirWithCorp.additionalDirectorInformation?.isResponsibleParty &&
            !selectedDirectorIds.includes(dirWithCorp.directorId)
        )
      )
      ?.map((each) => each.id);

    // List of directors that are currently selected and are not assigned as responsible party
    const directorsToAddAsResponsiblePartyUpdates: UpdateMultipleDirectorInputType[] =
      [
        ...selectedDirectorIds.map((eachDirId) => ({
          id: eachDirId,
          wrkReqId: data.id,
          corporationHasDirector: [
            {
              corporationId: data.corporationId,
              directorId: eachDirId,
              additionalDirectorInformation: {
                isDirector: isDirector(eachDirId),
                isResponsibleParty: true,
              },
            },
          ],
        })),
      ];

    // List of directors that are currently assigned as responsible party but are no longer selected
    const directorsToRemoveAsResponsiblePartyUpdates = [
      ...directorsToRemoveAsResponsibleParty.map((eachDirId) => ({
        id: eachDirId,
        wrkReqId: data.id,
        corporationHasDirector: [
          {
            corporationId: data.corporationId,
            directorId: eachDirId,
            additionalDirectorInformation: {
              isDirector: isDirector(eachDirId),
              isResponsibleParty: false,
            },
          },
        ],
      })),
    ];

    const directorUpdatesPayload = [
      ...directorsToAddAsResponsiblePartyUpdates,
      ...directorsToRemoveAsResponsiblePartyUpdates,
    ];

    const response = await updateMultipleDirectors(
      directorUpdatesPayload,
      token
    );

    if (!response) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong, Please try again later or contact Team Commenda if you need help',
      });
      setSubmitting(false);
      return;
    }

    await mutate();
    next();
    setSubmitting(false);
  };

  const hasDirectors = allCompanyDirectors?.directors?.length;

  return (
    <div className="flex flex-col gap-6 max-w-[700px]">
      <IncorporationFormHeading
        text={'Add Responsible Party'}
        additionalText="Please select someone who owns or controls your business and manages its funds and assets. This is usually the CEO.
If your responsible party has a US Social Security Number or ITIN, we will be able to procure your EIN more quickly."
      />

      {hasDirectors ? (
        <MultiSelect
          id="add-director-multiselect"
          options={(allCompanyDirectors?.directors || []).map((dir) => ({
            label: getDirectorName(dir),
            value: dir.id.toString(),
          }))}
          onValueChange={(val: string[]) => {
            setSelectedDirectorIds(val.map(Number));
            selectedDirectorIdsRef.current = val.map(Number);
          }}
          defaultValue={selectedDirectorIds.map(String)}
          hideSelectAll
          hideTags
          addItemConfig={{
            shouldAllowAdd: selectedDirectorIds.length < 1, // New RP can only be added if there not an already selected responsible party
            onAddItem: () => {
              setDirectorToEdit(null);
              directorModalToggle.on();
            },
            placeholder: 'Add new responsible party',
          }}
          placeholder="Add or select responsible party"
          maxSelectable={1}
        />
      ) : (
        <Button
          variant="secondary"
          className="w-full flex gap-2 items-center justify-center"
          onClick={() => {
            setDirectorToEdit(null);
            directorModalToggle.on();
          }}
        >
          <PlusIcon className="text-neutral-1" />
          <span>Add responsible party</span>
        </Button>
      )}

      <div>
        {directors
          ?.filter((dir) => selectedDirectorIds.includes(dir.id))
          .map((director) => (
            <DirectorCardKS
              directorForm={USResponsiblePartyForm}
              key={director.id}
              director={director}
              companyDataMutate={companyMutate}
              setNotification={setNotification}
              corporationId={corporationId}
              companyId={companyId}
              token={token}
              hasError={directorIdsWithError.has(director.id)}
              onErrorResolve={() => {
                setDirectorIdsWithError((prevIds) => {
                  const newIds = new Set(prevIds);
                  newIds.delete(director.id);
                  return newIds;
                });
                // Set the selected director ids to the current selected director ids
                setSelectedDirectorIds(selectedDirectorIds);
              }}
              onRemoveDirector={async (directorId: number) => {
                setSelectedDirectorIds((prev) =>
                  prev.filter((dirId) => dirId !== director.id)
                );
              }}
              workRequestData={data}
            />
          ))}
      </div>

      <div className="max-w-[700px]">
        {directorModalToggle.visible && (
          <DirectorModal
            isOpen={directorModalToggle.visible}
            setOpen={(isOpen) => {
              if (isOpen) {
                directorModalToggle.on();
              } else {
                directorModalToggle.off();
                setDirectorToEdit(null);
              }
            }}
            companyDataMutate={companyMutate}
            DirectorForm={USResponsiblePartyForm}
            setNotification={setNotification}
            companyId={companyId}
            corporationId={corporationId}
            token={token}
            selectedDirectorIds={selectedDirectorIds}
            setSelectedDirectorIds={setSelectedDirectorIds}
            director={directorToEdit}
            workRequestData={data}
            modalConfig={{
              isDirector: false,
              heading: directorToEdit
                ? 'Edit responsible party information'
                : 'Add responsible party',
              subheading: directorToEdit
                ? 'Edit details below to update responsible party'
                : 'Add a new responsible party to your company',
              postSave: () => {
                if (directorToEdit) {
                  setDirectorIdsWithError((prevIds) => {
                    const newIds = new Set(prevIds);
                    newIds.delete(directorToEdit.id);
                    return newIds;
                  });
                }
                setDirectorToEdit(null);
              },
            }}
          />
        )}
      </div>

      <div className="flex gap-4">
        <Button variant="secondary" onClick={previous}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back</span>
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isSubmitting || directors?.length === 0}
        >
          {isSubmitting ? <LoadingIndicator size={32} /> : null}
          <span className={classNames(isSubmitting ? 'ml-2' : '')}>
            Proceed
          </span>
          {!isSubmitting && <ProceedIcon className="ml-2 h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
};

export default ResponsiblePartyInfo;
